import { useState, useEffect } from 'react';

import { inject } from '@yoweb/utils/scripts';
import { runMocks } from '@yoweb/utils/common';

export type ReCaptcha = {
  execute: (action: string) => Promise<string | undefined>;
  error?: string;
};

/**
 * Adds the ReCaptcha script to the page allows executing ReCaptcha actions.
 */
export const useReCaptcha = (recaptchaKey?: string): ReCaptcha => {
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (runMocks) {
      const mockRecaptcha = {
        ready: (cb: () => void) => cb(),
        execute: () => Promise.resolve('recaptcha-token'),
      };
      window.grecaptcha = mockRecaptcha;
      return;
    }

    if (!recaptchaKey) {
      setError('ReCaptcha site key not set');
      return;
    }

    const injectReCaptcha = async () => {
      await inject(
        `https://www.google.com/recaptcha/api.js?render=${recaptchaKey}`,
        'google-recaptcha',
      );
    };

    injectReCaptcha();
  }, [recaptchaKey]);

  const execute = (action: string): Promise<string | undefined> =>
    new Promise<string | undefined>((resolve) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(recaptchaKey as string, { action }).then(resolve);
      });
    });

  return { error, execute };
};

export default useReCaptcha;
